import React from 'react';
import chroma from 'chroma-js';
import tenantTheme from '@theme';
import tenantConstants from '@constants';
import { t } from 'i18next';
import { isUnifiedProfolioEnabled } from '../../../utility/user';
import { getTimeDateString } from '../../../utility/date';
import { Flex, Icon, Tag } from '../../common';
import { v2Color } from '@theme/v2';

const resolveStatusColorForChroma = (color) => {
  if (color == null || color === '') {
    return null;
  }
  if (typeof color !== 'string') {
    return color;
  }
  const semantic = color.trim().toLowerCase();
  const semanticMap = {
    warning: tenantTheme['warning-color'],
    success: tenantTheme['success-color'],
    error: tenantTheme['error-color'],
    danger: tenantTheme['error-color'],
    default: tenantTheme.gray500 ?? tenantTheme['gray-500'],
    processing: tenantTheme['primary-color'],
    primary: tenantTheme['primary-color'],
    info: tenantTheme['info-color'],
  };
  if (semanticMap[semantic]) {
    return semanticMap[semantic];
  }
  try {
    if (chroma.valid(color)) {
      return color;
    }
  } catch {
    /* ignore */
  }
  return tenantTheme.gray600 ?? v2Color('#767676');
};

const getStatusTagStyle = (color) => {
  const resolved = resolveStatusColorForChroma(color);
  let bg = v2Color('#767676');
  if (resolved) {
    try {
      bg = chroma.mix(resolved, '#fff', 0.9).saturate(0.3).hex();
    } catch {
      bg = v2Color('#767676');
    }
  }
  return {
    '--tag-color': resolved || '#fff',
    '--tag-bg': bg,
    minWidth: '58px',
    height: '28px',
    borderRadius: '6px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '400',
    fontSize: '12px',
    padding: '0 8px',
    whiteSpace: 'nowrap',
  };
};

export const UnifiedListingStatusBadges = (props) => {
  if (isUnifiedProfolioEnabled({ featureKey: 'listingsUnified' })) {
    const status = props?.unified?.status;
    const disposition = props?.unified?.disposition;
    const key = disposition?.slug || disposition?.label;
    if (!key) {
      return null;
    }
    // Deleted listings: their last update is when they were removed.
    const deletedOn =
      tenantConstants.SHOW_DELETED_ON_DATE && disposition?.slug === 'deleted'
        ? getTimeDateString(props?.unified?.updatedAt)
        : '';
    return (
      <Flex
        gap="6px"
        wrap={!deletedOn}
        vertical={!!deletedOn}
        align={deletedOn ? 'flex-start' : 'center'}
        style={{ width: '100%', justifyContent: 'flex-start' }}
      >
        <Tag key={key} style={getStatusTagStyle(status?.color)}>
          {disposition?.label}
        </Tag>
        {!!deletedOn && (
          <Flex align="center" gap="6px" style={{ fontSize: 13, color: '#595959', whiteSpace: 'nowrap' }}>
            <Icon icon="FiCalendar" size={14} color="#f73131" aria-hidden />
            <span>{t('Deleted on {{date}}', { date: deletedOn })}</span>
          </Flex>
        )}
      </Flex>
    );
  }

  const platformEntries = Object.entries(props || {}).filter(([, value]) => value && typeof value === 'object' && value.status);
  const uniqueStatuses = [];
  const seen = new Set();

  platformEntries.forEach(([, platformData]) => {
    const status = platformData?.status;
    const key = status?.slug || status?.label;
    if (!key || seen.has(key)) return;
    seen.add(key);
    uniqueStatuses.push(status);
  });

  return (
    <Flex gap="6px" wrap justify="center" align="center" style={{ width: '100%', justifyContent: 'center' }}>
      {uniqueStatuses.map((status) => (
        <Tag key={status?.slug || status?.label} style={getStatusTagStyle(status?.color)}>
          {status?.label}
        </Tag>
      ))}
    </Flex>
  );
};

